import { useEffect, useState, useRef } from "react";
import "@/App.css";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// MFK Group Landing Page Component
const MFKLandingPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: ""
  });
  const [formStatus, setFormStatus] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const revealRefs = useRef([]);

  // Scroll reveal effect
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, i) => {
          if (entry.isIntersecting) {
            setTimeout(() => entry.target.classList.add("visible"), i * 80);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    revealRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  // Nav scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const nav = document.querySelector("nav");
      if (nav) {
        nav.style.boxShadow =
          window.scrollY > 10 ? "0 1px 20px rgba(0,0,0,0.08)" : "none";
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ESC key to close modals
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setPrivacyModalOpen(false);
        setTermsModalOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: "", message: "" });

    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        company: formData.company || null,
        message: formData.message
      });

      setFormStatus({
        type: "success",
        message: "Thank you! Your message has been sent successfully. We'll get back to you within 24 hours."
      });
      setFormData({ name: "", email: "", company: "", message: "" });
    } catch (error) {
      setFormStatus({
        type: "error",
        message: error.response?.data?.detail || "Something went wrong. Please try again or email us directly at contact@mfkgroupinc.com"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const addToRefs = (el) => {
    if (el && !revealRefs.current.includes(el)) {
      revealRefs.current.push(el);
    }
  };

  return (
    <>
      {/* NAV */}
      <nav>
        <a href="#" className="nav-logo" data-testid="nav-logo">MFK Group Inc.</a>
        <ul className="nav-links">
          <li><a href="#services" data-testid="nav-services">Services</a></li>
          <li><a href="#about" data-testid="nav-about">About</a></li>
          <li><a href="#how" data-testid="nav-how">How It Works</a></li>
          <li><a href="#contact" className="nav-cta" data-testid="nav-get-started">Get Started</a></li>
        </ul>
        <button className="hamburger" aria-label="Menu" data-testid="hamburger-menu">
          <span></span><span></span><span></span>
        </button>
      </nav>

      {/* HERO */}
      <div className="hero">
        <div className="hero-grid"></div>
        <div className="hero-badge" data-testid="hero-badge">AI-Powered Lead Generation & Software</div>
        <h1 data-testid="hero-title">Build smarter.<br /><em>Grow faster.</em></h1>
        <p data-testid="hero-subtitle">MFK Group Inc. combines cutting-edge AI with proven lead generation strategies and custom software development to accelerate your business growth.</p>
        <div className="hero-actions">
          <a href="#contact" className="btn-primary" data-testid="hero-start-project">Start a Project</a>
          <a href="#services" className="btn-secondary" data-testid="hero-explore-services">Explore Services</a>
        </div>
      </div>

      {/* CAPABILITIES BAR */}
      <div className="logos">
        <p>What we specialize in</p>
        <div className="logos-grid" data-testid="capabilities-grid">
          <span>Lead Generation</span>
          <span>Software Dev</span>
          <span>AI Integration</span>
          <span>CRM Automation</span>
          <span>Growth Analytics</span>
        </div>
      </div>

      {/* SERVICES */}
      <div className="services-section" id="services">
        <div className="services-inner">
          <div className="services-header reveal" ref={addToRefs}>
            <div>
              <p className="section-label">What We Do</p>
              <h2 className="section-title" data-testid="services-title">Services built for scale</h2>
            </div>
            <p className="section-subtitle">From generating high-quality leads to building enterprise-grade software, we deliver results that move the needle.</p>
          </div>
          <div className="services-grid reveal" ref={addToRefs} data-testid="services-grid">
            <div className="service-card" data-testid="service-lead-gen">
              <div className="service-icon">🎯</div>
              <h3>AI Lead Generation</h3>
              <p>Precision-targeted lead acquisition powered by machine learning models that identify and qualify your ideal customers at scale.</p>
            </div>
            <div className="service-card" data-testid="service-software-dev">
              <div className="service-icon">⚙️</div>
              <h3>Software Development</h3>
              <p>Custom web and mobile applications built with modern stacks — from MVP to enterprise, delivered on time and on budget.</p>
            </div>
            <div className="service-card" data-testid="service-ai-integration">
              <div className="service-icon">🤖</div>
              <h3>AI Integration</h3>
              <p>Embed intelligent automation into your existing workflows. Reduce manual effort and unlock new capabilities with AI.</p>
            </div>
            <div className="service-card" data-testid="service-analytics">
              <div className="service-icon">📊</div>
              <h3>Growth Analytics</h3>
              <p>Data-driven insights that reveal exactly where your growth is coming from and where to invest next for maximum ROI.</p>
            </div>
            <div className="service-card" data-testid="service-crm">
              <div className="service-icon">🔗</div>
              <h3>CRM & Automation</h3>
              <p>Seamless CRM setup, pipeline automation, and email sequences that nurture leads from first touch to closed deal.</p>
            </div>
            <div className="service-card" data-testid="service-consulting">
              <div className="service-icon">🛡️</div>
              <h3>Tech Consulting</h3>
              <p>Strategic technology advisory to help you make the right build vs. buy decisions and architect systems that scale.</p>
            </div>
          </div>
        </div>
      </div>

      {/* WHY US */}
      <div className="stats-section">
        <div className="stats-inner">
          <div className="reveal stats-left" ref={addToRefs}>
            <h2 data-testid="stats-title">Why businesses choose MFK Group</h2>
            <p>We combine technical depth with growth expertise — so you get a team that can build the software and fill your pipeline at the same time.</p>
            <a href="#contact" className="btn-white" data-testid="stats-work-with-us">Work With Us</a>
          </div>
          <div className="stats-grid reveal" ref={addToRefs} data-testid="stats-grid">
            <div className="stat-card">
              <div className="stat-number">AI</div>
              <div className="stat-label">AI-powered lead generation</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">B2B</div>
              <div className="stat-label">Specialized in B2B growth</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">CAD</div>
              <div className="stat-label">Canadian-based company</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">24/7</div>
              <div className="stat-label">Automated systems that never stop</div>
            </div>
          </div>
        </div>
      </div>

      {/* HOW IT WORKS */}
      <div className="how-section" id="how">
        <div className="how-inner">
          <div className="how-header reveal" ref={addToRefs}>
            <p className="section-label">The Process</p>
            <h2 className="section-title" data-testid="how-title">From discovery to results</h2>
          </div>
          <div className="steps" data-testid="steps-grid">
            <div className="step reveal" ref={addToRefs}>
              <div className="step-line"></div>
              <div className="step-number">01</div>
              <h3>Discovery Call</h3>
              <p>We start with a deep-dive into your business goals, current challenges, and growth targets to define a clear brief.</p>
            </div>
            <div className="step reveal" ref={addToRefs}>
              <div className="step-line"></div>
              <div className="step-number">02</div>
              <h3>Strategy & Plan</h3>
              <p>Our team builds a custom roadmap combining lead generation tactics and software requirements tailored to your market.</p>
            </div>
            <div className="step reveal" ref={addToRefs}>
              <div className="step-line"></div>
              <div className="step-number">03</div>
              <h3>Build & Launch</h3>
              <p>We execute rapidly — shipping software and activating lead pipelines with regular check-ins and transparent progress.</p>
            </div>
            <div className="step reveal" ref={addToRefs}>
              <div className="step-number">04</div>
              <h3>Optimize & Scale</h3>
              <p>Continuous iteration based on real data ensures your results compound over time. We grow as you grow.</p>
            </div>
          </div>
        </div>
      </div>

      {/* ABOUT */}
      <div className="about-section" id="about">
        <div className="about-inner">
          <div className="about-visual reveal" ref={addToRefs}>
            <div className="about-visual-text">MFK</div>
          </div>
          <div className="about-content reveal" ref={addToRefs}>
            <p className="section-label">About Us</p>
            <h2 className="section-title" data-testid="about-title">We're builders and growth operators</h2>
            <p className="section-subtitle">MFK Group Inc. is an AI-first technology company specializing in lead generation and custom software development. We help businesses leverage modern AI tools to find more customers and build better products.</p>
            <ul className="about-list" data-testid="about-list">
              <li>Founded with a mission to make AI-powered growth accessible to every business</li>
              <li>Team of engineers, growth strategists, and AI specialists</li>
              <li>Deep expertise in B2B lead generation across multiple industries</li>
              <li>End-to-end software development from concept to production</li>
              <li>Committed to transparent, results-driven partnerships</li>
            </ul>
          </div>
        </div>
      </div>

      {/* CONTACT FORM */}
      <div className="contact-form-section" id="contact">
        <div className="contact-form-inner">
          <div className="contact-form-content reveal" ref={addToRefs}>
            <p className="section-label">Get In Touch</p>
            <h2 className="section-title" data-testid="contact-title">Ready to grow with AI?</h2>
            <p className="section-subtitle">Book a free discovery call and let's map out exactly how MFK Group Inc. can accelerate your business in the next 90 days.</p>
            <div className="contact-info">
              <div className="contact-info-item">
                <span>Email:</span> <a href="mailto:contact@mfkgroupinc.com" data-testid="contact-email">contact@mfkgroupinc.com</a>
              </div>
              <div className="contact-info-item">
                <span>Response time:</span> Within 24 hours
              </div>
            </div>
          </div>
          <div className="contact-form reveal" ref={addToRefs} data-testid="contact-form">
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input 
                  type="text" 
                  id="name" 
                  name="name" 
                  placeholder="John Doe" 
                  required 
                  value={formData.name}
                  onChange={handleInputChange}
                  data-testid="contact-name-input" 
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input 
                  type="email" 
                  id="email" 
                  name="email" 
                  placeholder="john@company.com" 
                  required 
                  value={formData.email}
                  onChange={handleInputChange}
                  data-testid="contact-email-input" 
                />
              </div>
              <div className="form-group">
                <label htmlFor="company">Company Name</label>
                <input 
                  type="text" 
                  id="company" 
                  name="company" 
                  placeholder="Acme Inc." 
                  value={formData.company}
                  onChange={handleInputChange}
                  data-testid="contact-company-input" 
                />
              </div>
              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea 
                  id="message" 
                  name="message" 
                  placeholder="Tell us about your project or goals..." 
                  required 
                  value={formData.message}
                  onChange={handleInputChange}
                  data-testid="contact-message-input"
                ></textarea>
              </div>
              <button 
                type="submit" 
                className="form-submit" 
                disabled={isSubmitting}
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? "Sending..." : "Send Message"}
              </button>
              {formStatus.message && (
                <div 
                  className={`form-message ${formStatus.type}`} 
                  data-testid="form-message"
                >
                  {formStatus.message}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="cta-section">
        <div className="cta-inner reveal" ref={addToRefs}>
          <h2 data-testid="cta-title">Let's build something great</h2>
          <p>Prefer email? Reach out directly and we'll get back to you within 24 hours.</p>
          <div className="cta-actions">
            <a href="mailto:hello@mfkgroupinc.com" className="btn-white" data-testid="cta-book-call">Book a Call</a>
            <a href="mailto:hello@mfkgroupinc.com" className="btn-outline-white" data-testid="cta-send-message">Send a Message</a>
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <footer>
        <div className="footer-inner">
          <div className="footer-top">
            <div className="footer-brand">
              <div className="nav-logo">MFK Group Inc.</div>
              <p>AI-powered lead generation and software development. We help businesses grow faster with intelligent technology.</p>
            </div>
            <div className="footer-col">
              <h4>Services</h4>
              <ul>
                <li><a href="#services">Lead Generation</a></li>
                <li><a href="#services">Software Development</a></li>
                <li><a href="#services">AI Integration</a></li>
                <li><a href="#services">Growth Analytics</a></li>
              </ul>
            </div>
            <div className="footer-col">
              <h4>Company</h4>
              <ul>
                <li><a href="#about">About</a></li>
                <li><a href="#how">How It Works</a></li>
                <li><a href="#contact">Contact</a></li>
                <li><a href="#privacy" onClick={(e) => { e.preventDefault(); setPrivacyModalOpen(true); }}>Privacy Policy</a></li>
                <li><a href="#terms" onClick={(e) => { e.preventDefault(); setTermsModalOpen(true); }}>Terms of Service</a></li>
              </ul>
            </div>
            <div className="footer-col">
              <h4>Products</h4>
              <ul>
                <li><a href="https://mfk-group.com" target="_blank" rel="noopener noreferrer">SkillVault</a></li>
                <li><a href="#contact">Enterprise</a></li>
                <li><a href="mailto:contact@mfkgroupinc.com">API Access</a></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>© 2026 MFK Group Inc. All rights reserved. — <a href="mailto:contact@mfkgroupinc.com" style={{color: 'var(--gray-400)', textDecoration: 'none'}}>contact@mfkgroupinc.com</a></p>
            <div style={{display: 'flex', gap: '1.5rem'}}>
              <a href="#" onClick={(e) => { e.preventDefault(); setPrivacyModalOpen(true); }} style={{fontSize: '0.8rem', color: 'var(--gray-400)', textDecoration: 'none', fontFamily: 'var(--font-mono)'}}>Privacy Policy</a>
              <a href="#" onClick={(e) => { e.preventDefault(); setTermsModalOpen(true); }} style={{fontSize: '0.8rem', color: 'var(--gray-400)', textDecoration: 'none', fontFamily: 'var(--font-mono)'}}>Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>

      {/* PRIVACY MODAL */}
      <div 
        className={`modal-overlay ${privacyModalOpen ? 'open' : ''}`} 
        onClick={(e) => e.target === e.currentTarget && setPrivacyModalOpen(false)}
      >
        <div className="modal">
          <div className="modal-header">
            <div>
              <h2>Privacy Policy</h2>
              <p>Effective Date: January 1, 2025</p>
            </div>
            <button className="modal-close" onClick={() => setPrivacyModalOpen(false)} data-testid="privacy-modal-close">✕</button>
          </div>
          <div className="modal-body">
            <p>MFK INC. ("we," "us," or "our") respects your privacy. This Privacy Policy explains how we collect, use, and protect your personal information when you visit our website or use our services.</p>

            <h3>1. Information We Collect</h3>
            <p><strong>Information You Provide:</strong> When you contact us, we collect your name, email address, company name (if provided), message content, and service preferences.</p>
            <p><strong>Information Collected Automatically:</strong> IP address, browser type and version, device information, pages visited and time spent, referring website, and general location data (city/region level).</p>

            <h3>2. How We Use Your Information</h3>
            <ul>
              <li>Respond to your inquiries and provide requested information</li>
              <li>Deliver our lead generation and software development services</li>
              <li>Improve our website and services</li>
              <li>Send relevant business communications (with your consent)</li>
              <li>Analyze website usage and trends</li>
              <li>Comply with legal obligations</li>
            </ul>

            <h3>3. Cookies and Tracking Technologies</h3>
            <p>Our website uses cookies to remember preferences, analyze traffic, improve functionality, and deliver relevant advertising. We use Google Analytics to understand how visitors use our website. You can opt out of personalized advertising at <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer">adssettings.google.com</a>.</p>

            <h3>4. How We Share Your Information</h3>
            <p>We do not sell, rent, or trade your personal information. We may share with service providers who help operate our business, legal authorities when required by law, and business successors in the event of a merger or acquisition.</p>

            <h3>5. Data Security</h3>
            <p>We implement reasonable security measures to protect your personal information. However, no internet transmission is completely secure.</p>

            <h3>6. Data Retention</h3>
            <p>Contact form submissions are typically retained for 2–3 years unless you request deletion.</p>

            <h3>7. Your Privacy Rights</h3>
            <p>Under Canadian privacy principles (PIPEDA), you have the right to access, correct, or request deletion of your personal information. Contact us at <a href="mailto:contact@mfkgroupinc.com">contact@mfkgroupinc.com</a>.</p>

            <h3>8. Children's Privacy</h3>
            <p>Our services are not directed to individuals under 18. We do not knowingly collect personal information from children.</p>

            <h3>9. Contact Us</h3>
            <p>Email: <a href="mailto:contact@mfkgroupinc.com">contact@mfkgroupinc.com</a> — Website: mfkgroupinc.com</p>
          </div>
          <div className="modal-footer">
            <p>Compliant with PIPEDA (Canadian Privacy Law)</p>
            <button className="btn-primary" style={{fontSize: '0.85rem', padding: '0.5rem 1.25rem'}} onClick={() => setPrivacyModalOpen(false)}>Close</button>
          </div>
        </div>
      </div>

      {/* TERMS MODAL */}
      <div 
        className={`modal-overlay ${termsModalOpen ? 'open' : ''}`}
        onClick={(e) => e.target === e.currentTarget && setTermsModalOpen(false)}
      >
        <div className="modal">
          <div className="modal-header">
            <div>
              <h2>Terms of Service</h2>
              <p>Effective Date: January 1, 2025</p>
            </div>
            <button className="modal-close" onClick={() => setTermsModalOpen(false)} data-testid="terms-modal-close">✕</button>
          </div>
          <div className="modal-body">
            <p>Welcome to MFK INC. These Terms of Service govern your use of our website and the services we provide. By accessing our website or engaging our services, you agree to these Terms.</p>

            <h3>1. Services</h3>
            <p>MFK INC. provides lead generation services (paid advertising, landing page optimization, call tracking, CRM integration, local SEO) and software development services (custom websites, web applications, automation systems, API integrations, AI-powered tools).</p>

            <h3>2. No Guarantee of Results</h3>
            <p>Our services are provided on a best-efforts basis. We do not guarantee specific lead volumes, conversion rates, or business outcomes. Past results do not guarantee future performance.</p>

            <h3>3. Client Responsibilities</h3>
            <ul>
              <li>Provide accurate information and timely feedback</li>
              <li>Cooperate in good faith throughout the project</li>
              <li>Grant necessary access to systems and resources</li>
              <li>Pay invoices according to agreed terms</li>
              <li>Use our services lawfully and ethically</li>
            </ul>

            <h3>4. Payment Terms</h3>
            <p>Invoices are due within 15 days of receipt. Late payments may result in suspension of services. All fees are in Canadian dollars (CAD) unless otherwise stated.</p>

            <h3>5. Intellectual Property</h3>
            <p>Deliverables created specifically for you become your property upon full payment. We retain ownership of our proprietary processes, frameworks, and pre-existing tools.</p>

            <h3>6. Confidentiality</h3>
            <p>Both parties agree to keep confidential information private and use it only for the purposes of the engagement.</p>

            <h3>7. Limitation of Liability</h3>
            <p>Our liability is limited to the fees paid for the specific service that gave rise to the claim. We are not liable for indirect, incidental, or consequential damages.</p>

            <h3>8. Termination</h3>
            <p>Either party may terminate services with written notice. Fees for work completed are due upon termination.</p>

            <h3>9. Governing Law</h3>
            <p>These Terms are governed by the laws of Canada. Any disputes will be resolved in Canadian courts.</p>

            <h3>10. Contact</h3>
            <p>Email: <a href="mailto:contact@mfkgroupinc.com">contact@mfkgroupinc.com</a> — Website: mfkgroupinc.com</p>
          </div>
          <div className="modal-footer">
            <p>Governed by Canadian law</p>
            <button className="btn-primary" style={{fontSize: '0.85rem', padding: '0.5rem 1.25rem'}} onClick={() => setTermsModalOpen(false)}>Close</button>
          </div>
        </div>
      </div>
    </>
  );
};

function App() {
  return <MFKLandingPage />;
}

export default App;
